import type { ErrorLike } from "@apollo/client";
import {
  CombinedGraphQLErrors,
  CombinedProtocolErrors,
  ServerError,
  ServerParseError,
} from "@apollo/client";

type ApolloFunctionReturn<T> = {
  data?: T | undefined;
  error?: ErrorLike | undefined;
};

// Apollo の結果を扱いやすいようにラップする
//
// - useMutation, useLazyQuery から得られる リクエスト実行関数は、エラー時に throw する（errorPolicy にもよる）が、多くの場合、 try catch をするのは面倒なので、戻り値として扱えるようにする
//   - https://www.apollographql.com/docs/react/data/error-handling
// - data と error を Union 型で返す
//   - 結果は排他的になるので、 errorPolicy: all で部分的な結果を得たいケースでは注意する
export const handleApolloResult = async <T>(
  result: Promise<ApolloFunctionReturn<T>>,
): Promise<
  | {
      data: T;
      error: undefined;
    }
  | {
      data: undefined;
      error: ErrorLike;
    }
> => {
  let data: T | undefined;
  let error: ErrorLike | undefined;

  try {
    const { data: _data, error: _error } = await result;
    data = _data;
    error = _error;
  } catch (e) {
    // いくつかの予期できるエラーは、エラーオブジェクトして扱い、それ以外は throw して中断させる
    if (
      CombinedGraphQLErrors.is(e) ||
      CombinedProtocolErrors.is(e) ||
      ServerError.is(e) ||
      ServerParseError.is(e)
    ) {
      error = e;
    } else {
      throw e;
    }
  }

  if (typeof error !== "undefined") {
    return {
      data: undefined,
      error,
    };
  }

  if (typeof data !== "undefined") {
    return {
      data,
      error: undefined,
    };
  }

  // ここには来ないはず
  throw new Error("データが取得できませんでした");
};
